import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import { wilayas, getCommunes } from '../data/algeriaLocations';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || '';

const getImageUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
};

const Checkout = () => {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    phoneNumber: '',
    wilaya: '',
    commune: '',
    address: '',
  });

  const availableCommunes = useMemo(
    () => (form.wilaya ? getCommunes(form.wilaya) : []),
    [form.wilaya]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return;

    setSubmitting(true);
    try {
      // Format phone
      let formattedPhone = form.phoneNumber.replace(/\D/g, '');
      if (formattedPhone.startsWith('213')) {
        formattedPhone = '+' + formattedPhone;
      } else if (formattedPhone.startsWith('0')) {
        formattedPhone = '+213' + formattedPhone.slice(1);
      } else if (formattedPhone.length === 9 && /^[5-7]/.test(formattedPhone)) {
        formattedPhone = '+213' + formattedPhone;
      } else {
        formattedPhone = '+213' + formattedPhone;
      }

      if (!/^(\+213|0)[5-7][0-9]{8}$/.test(formattedPhone)) {
        toast.error('Please enter a valid Algerian phone number (e.g., 0555123456)');
        setSubmitting(false);
        return;
      }

      const subtotal = totalPrice;
      const total = subtotal;

      const orderItems = items.map((item) => ({
        product: item.product._id,
        size: item.size,
        quantity: item.quantity,
        price: item.product.salePrice || item.product.price,
      }));

      const notes = items
        .map(
          (item) =>
            `${item.product.name} — Size: ${item.size}${item.color ? `, Color: ${item.color}` : ''} x${item.quantity}`
        )
        .join(' | ');

      const orderData = {
        customer: {
          name: form.fullName,
          phone: formattedPhone,
          state: form.wilaya,
          city: form.commune,
          address: form.address,
        },
        items: orderItems,
        subtotal,
        deliveryFee: 0,
        total,
        notes,
      };

      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      const responseData = await response.json();

      if (response.ok && responseData.success) {
        const orderNumber = responseData.data?.orderNumber || 'N/A';
        clearCart();
        toast.success(`Order #${orderNumber} placed successfully! You will be contacted shortly.`, {
          duration: 5000,
        });
        navigate('/');
      } else {
        if (responseData.errors && Array.isArray(responseData.errors)) {
          const msgs = responseData.errors.map((err: { msg: string }) => err.msg).join('\n');
          throw new Error(msgs);
        }
        throw new Error(responseData.message || 'Failed to submit order');
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Error submitting order: ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 rounded-full bg-warmgray-200 flex items-center justify-center mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-warmgray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        </div>
        <p className="font-sans text-warmgray-500 mb-6">Your cart is empty</p>
        <Link to="/collections" className="btn-primary px-8 py-3">
          Browse Collections
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 md:py-16">
      {/* Back */}
      <Link
        to="/collections"
        className="inline-flex items-center gap-2 text-sm text-warmgray-500 hover:text-offblack transition-colors mb-8"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Continue Shopping
      </Link>

      <h1 className="font-display text-2xl md:text-3xl tracking-tight text-offblack mb-10">
        Checkout
      </h1>

      <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
        {/* ---- Form (left) ---- */}
        <div className="lg:col-span-3">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="input-label">Full Name *</label>
              <input
                type="text"
                required
                value={form.fullName}
                onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                className="input-field"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label className="input-label">Phone Number *</label>
              <input
                type="tel"
                required
                value={form.phoneNumber}
                onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                className="input-field"
                placeholder="0555 123 456"
              />
              <p className="text-xs text-warmgray-400 mt-1">Algerian mobile (05, 06, or 07)</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Wilaya *</label>
                <select
                  required
                  value={form.wilaya}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, wilaya: e.target.value, commune: '' }))
                  }
                  className="input-field"
                >
                  <option value="">Select</option>
                  {wilayas.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="input-label">Commune *</label>
                <select
                  required
                  value={form.commune}
                  onChange={(e) => setForm((p) => ({ ...p, commune: e.target.value }))}
                  className="input-field"
                  disabled={!form.wilaya}
                >
                  <option value="">{form.wilaya ? 'Select' : '—'}</option>
                  {availableCommunes.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="input-label">Address *</label>
              <textarea
                required
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                className="input-field"
                rows={2}
                placeholder="Complete delivery address"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full py-3.5 mt-4 disabled:opacity-50"
            >
              {submitting ? 'Placing Order...' : 'Confirm Order'}
            </button>
          </form>
        </div>

        {/* ---- Order Summary (right) ---- */}
        <div className="lg:col-span-2">
          <div className="bg-warmgray-50 p-6 sticky top-28">
            <h2 className="font-display text-lg tracking-tight text-offblack mb-5">
              Order Summary
            </h2>

            <ul className="space-y-4 mb-6">
              {items.map((item) => (
                <li
                  key={`${item.product._id}-${item.size}-${item.color}`}
                  className="flex gap-3"
                >
                  <div className="w-16 h-20 flex-shrink-0 bg-warmgray-200 overflow-hidden">
                    <img
                      src={getImageUrl(item.product.images?.[0] || '')}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-offblack truncate">
                      {item.product.name}
                    </p>
                    <p className="text-xs text-warmgray-400 mt-0.5">
                      {item.size && `Size: ${item.size}`}
                      {item.size && item.color && ' · '}
                      {item.color && `Color: ${item.color}`}
                    </p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-warmgray-500">Qty: {item.quantity}</span>
                      <span className="text-sm font-medium text-offblack">
                        {((item.product.salePrice || item.product.price) * item.quantity).toLocaleString()} DA
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-t border-warmgray-200 pt-4 space-y-2">
              <div className="flex justify-between text-sm font-sans">
                <span className="text-warmgray-500">Subtotal</span>
                <span className="text-offblack">{totalPrice.toLocaleString()} DA</span>
              </div>
              <div className="border-t border-warmgray-200 pt-2 flex justify-between font-sans font-medium">
                <span className="text-offblack">Total</span>
                <span className="text-offblack">{totalPrice.toLocaleString()} DA</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
